import React, { useEffect, useState, useContext, useImperativeHandle, forwardRef } from 'react';
import SendIcon from '@mui/icons-material/Send';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import './RightSidebar.css';
import ReactMarkdown from 'react-markdown';
import { trackEvent } from '../../../utils/eventTracking';
import { UserProfileContext } from '../../../UserProfileContext';

/**
 * Spinner
 * Simple animated loading indicator for async operations in chat.
 */
const Spinner = () => (
	<div className="spinner">
		<div className="bounce1" />
		<div className="bounce2" />
		<div className="bounce3" />
	</div>
);

/**
 * RightSidebar
 * Chat sidebar component for interacting with the SportWise Agent.
 * Handles chat UI, conversation state, and LangGraph API integration.
 *
 * Props:
 *   showModal (bool): Whether the sidebar is open.
 *   setShowModal (func): Function to toggle sidebar visibility.
 *   dataspaceid (string): Current dataspace context.
 *   jwt (string): User authentication token.
 *
 * Exposes:
 *   resetAndDeleteAssistant: Resets state and deletes agent on parent or tab close.
 */
const RightSidebar = forwardRef(function RightSidebar(props, ref) {
	const { showModal, setShowModal, dataspaceid, jwt } = props;

	const userProfile = useContext(UserProfileContext)[0];

	// User's current input in the chat box
	const [postContent, setPostContent] = useState('');
	// Array of all messages (user & agent) in this conversation
	const [conversation, setConversation] = useState([]);
	// LangGraph assistant instance for this session
	const [assistant_id, setAssistant_id] = useState('');
	// Thread ID for maintaining conversation context
	const [thread_id, setThread_id] = useState('');

	/**
	 * Handler for sending a new chat message.
	 * Ensures assistant and thread are created, then streams agent response.
	 * Updates conversation state with both user and agent messages.
	 * Handles Enter/Shift+Enter logic and disables input during streaming.
	 */

	/**
	 * Reset and delete assistant (exposed to parent and used for beforeunload)
	 * Deletes the agent instance from LangGraph and clears local state.
	 */
	const resetAndDeleteAssistant = async () => {
		if (assistant_id) {
			await deleteAssistant();
		}
		setAssistant_id('');
		setThread_id('');
		setConversation([]);
		setPostContent(''); // Also clear chat input
	};

	// Expose resetAndDeleteAssistant to parent via ref
	useImperativeHandle(ref, () => ({
		resetAndDeleteAssistant,
	}));

	// Cleanup: Delete assistant when page/tab is closed
	useEffect(() => {
		const handleBeforeUnload = async (e) => {
			// Attempt synchronous reset (fetch may not complete)
			await resetAndDeleteAssistant();
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [assistant_id]);

	const closeChatbotClickHandler = () => {
		setShowModal(false);
		trackEvent({
			userDetails: { userId: userProfile._id, email: userProfile.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights'],
				eventName: 'WebApp_User closed chatbot.',
			},
		});
	};

	/**
	 * deleteAssistant
	 * Makes API call to delete the assistant instance from LangGraph backend.
	 * Called when resetting or cleaning up the chat session.
	 */
	const deleteAssistant = async () => {
		try {
			const response = await fetch(`${process.env.REACT_APP_LANGGRAPH_API_BASE_URL}/assistants/${assistant_id}`, {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					'X-Api-Key': process.env.REACT_APP_LANGGRAPH_API_KEY,
				},
			});
			if (response.ok) {
				console.log('Assistant deleted successfully');
			} else {
				console.error('Error deleting assistant:', response.statusText);
			}
		} catch (error) {
			console.error('Error:', error);
		}
	};

	const submitMessageHandler = async () => {
		if (!postContent.trim()) return;

		trackEvent({
			userDetails: { userId: userProfile._id, email: userProfile.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'Encharge', 'AppInsights'],
				eventName: 'WebApp_User sent message to chatbot.',
			},
		});

		try {
			let newAssistantId = assistant_id;
			let currentThreadId = thread_id;

			console.log('➡️ Message submitted:', postContent);

			// 1️⃣ Create assistant (if not yet created)
			if (!assistant_id) {
				const response = await fetch(`${process.env.REACT_APP_LANGGRAPH_API_BASE_URL}/assistants`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Api-Key': process.env.REACT_APP_LANGGRAPH_API_KEY,
					},
					body: JSON.stringify({
						graph_id: 'agent',
						config: {
							configurable: {
								DataSpaceId: dataspaceid,
								jwt: jwt,
							},
						},
						metadata: {},
						if_exists: 'raise',
						name: 'sample',
					}),
				});

				const result = await response.json();
				newAssistantId = result.assistant_id;
				setAssistant_id(newAssistantId);
				console.log('✅ Created assistant:', newAssistantId);
			}

			// 2️⃣ Create thread if not yet created
			if (!thread_id) {
				const threadResponse = await fetch(`${process.env.REACT_APP_LANGGRAPH_API_BASE_URL}/threads`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Api-Key': process.env.REACT_APP_LANGGRAPH_API_KEY,
					},
					body: JSON.stringify({}),
				});

				const threadResult = await threadResponse.json();
				currentThreadId = threadResult.thread_id;
				setThread_id(currentThreadId);
				console.log('🧵 Created thread:', currentThreadId);
			}

			// 👤 Show user message
			const userMessageId = Date.now();
			setConversation((prev) => [...prev, { id: userMessageId, role: 'human', content: postContent }]);
			setPostContent('');

			// ⏳ Show spinner
			const placeholderId = userMessageId + 1;
			setConversation((prev) => [...prev, { id: placeholderId, role: 'bot', content: <Spinner /> }]);

			// 📨 Run message with reused thread
			console.log('📤 Sending to LangGraph → assistant:', newAssistantId, 'thread:', currentThreadId);

			const runPayload = {
				assistant_id: newAssistantId,
				thread_id: currentThreadId,
				config: {
					configurable: {
						DataSpaceId: dataspaceid,
						jwt: jwt,
					},
				},
				input: {
					messages: [
						{
							role: 'human',
							content: postContent,
						},
					],
				},
				stream_mode: 'updates',
			};

			/**
			 * Send a message to the LangGraph agent within the current thread context.
			 *
			 * Per LangGraph API docs (https://langchain-ai.github.io/langgraph/cloud/reference/api/api_ref.html),
			 * to maintain conversation history, POST to /threads/{thread_id}/runs/stream instead of /runs/stream.
			 * This ensures the run is associated with the correct thread and preserves chat history.
			 */
			const newresponse = await fetch(
				`${process.env.REACT_APP_LANGGRAPH_API_BASE_URL}/threads/${currentThreadId}/runs/stream`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Api-Key': process.env.REACT_APP_LANGGRAPH_API_KEY,
					},
					body: JSON.stringify(runPayload),
				},
			);

			// 🧠 Stream response
			if (newresponse.body) {
				const reader = newresponse.body.getReader();
				const decoder = new TextDecoder();
				let fullMessage = '';

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					const chunk = decoder.decode(value, { stream: true });
					const cleanedString = chunk
						.replace(/event: metadata\r\n/g, '')
						.replace(/event: updates\r\n/g, '')
						.replace(/data: /g, '')
						.trim();

					const jsonPartsString = cleanedString.split('\n\n');
					if (jsonPartsString[0].includes('heartbeat')) {
						console.log('💓 heartbeat');
					} else {
						try {
							const jsonPart = JSON.parse(jsonPartsString[0]);
							if ('chatbot' in jsonPart) {
								const msg = jsonPart['chatbot']['messages']?.[0]?.['content'];
								if (msg) {
									fullMessage = msg; // Always keep the latest full message
								}
							}
						} catch (e) {
							console.error('⚠️ Error parsing response:', e, jsonPartsString[0]);
						}
					}
				}
				// After stream is done, update the conversation to replace spinner with the full message
				setConversation((prevConversation) =>
					prevConversation.map((m) => (m.id === placeholderId ? { ...m, content: fullMessage } : m)),
				);
			}
		} catch (error) {
			console.error('❌ LangGraph error:', error);
		}
	};

	return (
		<div className={`modal-dialog${showModal ? ' show' : ''}`} role="dialog" aria-modal="true">
			<div className="modal-header">
				{/* Close Button for Sidebar */}
				<button
					className="close-button"
					aria-label="Close sidebar"
					onClick={closeChatbotClickHandler}
					tabIndex={0}
					type="button"
				>
					<CloseIcon fontSize="medium" />
				</button>
				<h3>SportWise Agent</h3>
			</div>
			<div className="modal-content">
				<div className="chat-body" role="log" aria-live="polite" tabIndex={0}>
					{/*
          Render the chat conversation history as message bubbles.
          User messages are aligned right with 👤, agent messages left with 🤖.
          Uses ReactMarkdown for rich formatting in agent responses.
        */}
					{conversation.map((message, index) => {
						const isUser = message.role === 'human';
						const avatar = isUser ? (
							<div className="avatar user-avatar" aria-label="You">
								👤
							</div>
						) : (
							<div className="avatar agent-avatar" aria-label="AI">
								🤖
							</div>
						);
						return (
							<div
								key={index}
								className={`chat-message ${isUser ? 'user' : 'agent'}`}
								tabIndex={0}
								aria-label={isUser ? 'Your message' : 'Agent message'}
							>
								{avatar}
								<div className="bubble">
									{typeof message.content === 'string' ? (
										<ReactMarkdown>{message.content}</ReactMarkdown>
									) : (
										message.content
									)}
								</div>
							</div>
						);
					})}
					<div
						ref={(el) => {
							if (el) el.scrollIntoView({ behavior: 'smooth', block: 'end' });
						}}
					/>
				</div>
				<div className="chat-input-container">
					<TextField
						label="How may I help you with your dataspace?"
						multiline
						minRows={2}
						maxRows={6}
						value={postContent}
						onChange={(e) => setPostContent(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								submitMessageHandler();
							}
						}}
						variant="outlined"
						fullWidth
						className="custom-textfield"
						InputLabelProps={{
							style: { fontSize: '0.8rem' },
						}}
						InputProps={{
							endAdornment: (
								<IconButton onClick={submitMessageHandler} className="send-button" aria-label="Send message">
									<SendIcon />
								</IconButton>
							),
						}}
						aria-label="Chat input"
					/>
				</div>
			</div>
		</div>
	);
});
export default RightSidebar;
